import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useStoreSelectionStore } from '@/stores/store-selection-store';
import { isPublicDomain } from '@/config/constants';

// Routes that require the user to have an active store session.
// If someone opens one of these URLs without having gone through postcode → store selection,
// they are redirected to the home page instead of seeing another user's context.
const STORE_CONTEXT_ROUTES = [
  '/menu',
  '/deals',
  '/pizza-details',
  '/chicken-dip-details',
  '/deal-detail',
  '/basket',
  '/checkout',
  '/payment',
  '/payment-failed',
  '/order-confirmation',
  '/track-order',
];

// ============================================================================
// PrivateRoute - Hostname-Aware + Store-Context Access Control
// ============================================================================
// PUBLIC DOMAINS (dominos.co.uk, dominos.ie):
//   → Always renders children — no login required
//   → Site behaves as a public customer-facing app
//
// ALL OTHER DOMAINS (pizzaapp-*.2sdtechnologies.com):
//   → Requires authentication — redirects to /login if not logged in
//   → Store-context routes also require hasConfirmedPostcode — redirects to /
//     if the user has no store selected (e.g. opened someone else's URL)
// ============================================================================

export default function PrivateRoute() {
  const location = useLocation();
  const initialize = useAuthStore((s) => s.initialize);
  const initialized = useAuthStore((s) => s.initialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loading = useAuthStore((s) => s.loading);
  const hasConfirmedPostcode = useStoreSelectionStore((s) => s.hasConfirmedPostcode);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  // Public domains: bypass auth entirely
  if (isPublicDomain()) {
    return <Outlet />;
  }

  // Private domains: wait for auth initialization
  if (!initialized || loading) {
    return null;
  }

  // Private domains: redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Store-context routes: redirect to home if user has no active store session.
  // This prevents accessing another user's menu/basket/checkout/order URLs.
  const isStoreContextRoute = STORE_CONTEXT_ROUTES.some((r) =>
    location.pathname.startsWith(r),
  );
  if (isStoreContextRoute && !hasConfirmedPostcode) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
